import { availableCoaches, resumeIndex, visibleCoachSteps } from './coach';
import type { GuidanceInternals } from './context';
import type { TourStatus } from './progress/store';
import type { CoachDefinition, CoachId } from './types';

/**
 * The coaches' public entry points (UI-6), for Help & tours, Quick Find's Help
 * group and anybody else who offers a coach: what this person may take, in
 * which state, and how to start or continue one. Every start goes through the
 * guide store — the engine does the rest, and only ever follows the person.
 */
export { availableCoaches };

export interface CoachEntry {
  def: CoachDefinition;
  /** Stored progress for this version: not started, in progress or completed. */
  status: TourStatus;
  /** Running now in this tab (perhaps minimised, or hidden behind a dialog). */
  running: boolean;
}

/** The coaches this person may take here, with their state. */
export function coachEntries(guidance: GuidanceInternals): CoachEntry[] {
  const active = guidance.store.getState().active;

  return availableCoaches(guidance.coaches ?? [], guidance.ctx).map((def) => ({
    def,
    status: guidance.progress.coachStatus(def),
    running: active !== null && active.mode === 'coach' && active.tourId === def.id,
  }));
}

function find(guidance: GuidanceInternals, id: CoachId): CoachDefinition | undefined {
  return availableCoaches(guidance.coaches ?? [], guidance.ctx).find((def) => def.id === id);
}

/**
 * Starts a coach at its first step (Start, and Restart — which undoes nothing
 * the person did; it only shows the way again). `false` when this person is
 * not offered it here.
 */
export function startCoach(
  guidance: GuidanceInternals,
  id: CoachId,
  options: { opener?: HTMLElement | null } = {},
): boolean {
  const def = find(guidance, id);
  const first = def === undefined ? undefined : visibleCoachSteps(def, guidance.ctx).at(0);

  if (def === undefined || first === undefined) return false;

  // A person taking a coach needs no first-visit invitation on top of it.
  guidance.progress.dismissInvitation();
  guidance.store.start({ tourId: def.id, mode: 'coach', stepId: first.id, opener: options.opener });

  return true;
}

/**
 * Continues a coach where it stopped. The run is RESUMED: no outcome of before
 * is assumed — the step is decided by its page and the server-rendered probes.
 */
export function continueCoach(
  guidance: GuidanceInternals,
  id: CoachId,
  options: { opener?: HTMLElement | null } = {},
): boolean {
  const def = find(guidance, id);

  if (def === undefined) return false;

  const steps = visibleCoachSteps(def, guidance.ctx);
  const stored = guidance.progress.coachResumeStep(def);
  const from = steps[resumeIndex(steps, stored)];

  if (from === undefined) return false;

  guidance.store.start({
    tourId: def.id,
    mode: 'coach',
    stepId: from.id,
    opener: options.opener,
    resumed: stored !== undefined,
  });

  return true;
}
